<template>
  <main class="educational-content resources__page-topic">
    <div class="resources__top-wrap">
      <RichRenderer :richTextData="cPage.fields.paragraphRich" />
      <form class="educational-content__search-form" @submit.prevent="doSearch">
        <input
          v-model="queryString"
          class="form-input form-input--small educational-content__search-input"
          type="search"
        />
        <app-button
          class="educational-content__search-button"
          text="Search"
          type="submit"
          size="small"
        />
        <app-button
          class="cancel-button educational-content__search-button"
          text="Reset"
          type="button"
          variant="white"
          size="small"
          @clicked="resetQuery"
        />
      </form>
      <facets
        class="search-page__facets"
        :facetsList="facets"
        :isOpenStateSticky="false"
        :isClickOutside="true"
        :searchWithinFacetValues="searchWithinFacetValues"
        :toggleItemExclusion="toggleItemExclusion"
        :toggleItemRefinement="toggleItemRefinement"
        checkboxSize="small"
        @facetRefined="onFacetUpdate"
      />
      <div v-if="queryFacets.length" class="chips">
        <div
          v-for="facet in queryFacets.flat()"
          :key="`${facet.facetTitle}-${facet.valueTitle}`"
          class="chips__item"
        >
          <span class="chips__text">{{ getTitleForValue(facet.valueTitle) }}</span>
          <i class="icon-close chips__delete" @click="onChipDelete(facet)" />
        </div>
      </div>
    </div>
    <hint-bar
      v-if="noResultsHintVisible"
      :hintData="noResultsHint"
      class="educational-content__hint-bar"
      @clicked="onHintResetClicked($event)"
    />
    <div
      v-for="pane in resultPanes.filter((pane) => pane.collection.length)"
      :key="pane.title"
      class="resources__content-list"
    >
      <loader v-if="isLoaderVisible" :inline="true" />
      <h3
        class="text-color-navy educational-content__topic-title resources__content-topic-title text-h4"
      >
        {{ pane.title }}
      </h3>
      <filter-wrapper
        v-if="pane.isFilterable"
        class="educational-content__filter"
        :optionsList="sorting"
        :isOrderByVisible="false"
      />

      <resource-item
        v-for="(item, n) in pane.collection"
        :key="item.title + n"
        :data="item"
        :showAuthor="item.resourceGroup === 'Blogs'"
        :showDate="item.resourceGroup === 'Blogs'"
        :isRichDescription="false"
        @loginGatedResourceRequested="onLoginGatedResourceRequested"
      />
    </div>
    <infinite-loader :disabled="isLoaderVisible" @infinite="showNextPage" />

    <transition name="modal">
      <login-gated-resource-modal
        v-if="isLoginGatedResourceModalVisible"
        :isGuestAccessVisible="!!loginGatedResourceData.guestUrl"
        @loggedIn="openLoginGatedResource('loggedIn')"
        @guestAccessRequested="openLoginGatedResource('guest')"
        @close="isLoginGatedResourceModalVisible = false"
      />
    </transition>
  </main>
</template>
<script>
import arraySort from 'array-sort'
import { computed, nextTick, ref } from 'vue'

import { debounce } from '@/lib/utils/debounce'

import Facets from '@/components/common/Facets.vue'
import FilterWrapper from '@/components/common/FilterWrapper.vue'
import HintBar from '@/components/common/HintBar.vue'
import InfiniteLoader from '@/components/common/InfiniteLoader.vue'

import LoginGatedResourceModal from '@/components/auth/LoginGatedResourceModal.vue'
import ResourceItem from '@/components/resources/pages/ResourceItem.vue'

import { useLoginGatedResource } from '@/composables/useLoginGatedResource'
import { useOfflinePagination } from '@/composables/useOfflinePagination'
import { usePageMeta } from '@/composables/usePageMeta'
import { useSearch } from '@/composables/useSearch'
import { useSearchService } from '@/composables/useSearchService'
import { useSorting } from '@/composables/useSorting'
import { useSortingMethods } from '@/composables/useSortingMethods'
import { useTransformEntries } from '@/composables/useTransformEntries'
import educationalConentFacetMap from '@/settings/algolia/educational-content-facet-map'

const _featuredPaneTitle = 'FEATURED CONTENT'
const _additionalPaneTitle = 'ADDITIONAL CONTENT'

export default {
  name: 'EducationContent',
  components: {
    Facets,
    FilterWrapper,
    HintBar,
    LoginGatedResourceModal,
    ResourceItem,
    InfiniteLoader,
  },
  props: {
    cPage: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { setPageMeta } = usePageMeta()
    const { buffer, getFirstSet, getNextSet, initPagination } = useOfflinePagination()
    const { withSortByFeaturedAndDate, sortByDateDesc } = useSortingMethods()
    const { transformEntries } = useTransformEntries()
    const search = useSearchService()

    const {
      loadAllEntries,
      fetchResources,
      getORFacetGroupString,
      getQueryStringFromUrl,
      getFacetsFromUrl,
      groupFacets,
      searchWithinFacetValues,
      toggleItemExclusion,
      toggleItemRefinement,
      getQueryFacetsFromSelection,
      getTitleForValue,
      clearFacets,
      facetMap,
    } = useSearch({
      activeIndexGetter: search.service.getResourceIndex,
      customFacetMap: JSON.parse(JSON.stringify(educationalConentFacetMap)),
    })
    const {
      loginGatedResourceData,
      isLoginGatedResourceModalVisible,
      onLoginGatedResourceRequested,
      openLoginGatedResource,
    } = useLoginGatedResource()

    const isInitialized = ref(false)
    const isLoaderVisible = ref(false)
    const facets = ref([])
    const noResultsHint = ref({
      message: 'No results found. <a id="reset-search">Click here to reset</a>.',
      variant: 'warning',
    })
    const queryString = ref(getQueryStringFromUrl())
    const queryFacets = ref([])
    const resultPanes = ref([
      {
        title: _featuredPaneTitle,
        collection: [],
        isFilterable: false,
      },
      {
        title: _additionalPaneTitle,
        collection: [],
        isFilterable: true,
      },
    ])

    const noResultsHintVisible = computed(() => {
      return (
        isInitialized.value &&
        !additionalResources.value.collection.length &&
        !featuredResources.value.collection.length
      )
    })
    const additionalResources = computed(() => {
      return resultPanes.value.find((el) => el.title === _additionalPaneTitle)
    })
    const featuredResources = computed(() => {
      return resultPanes.value.find((el) => el.title === _featuredPaneTitle)
    })

    const showFirstPage = () => {
      const firstSet = getFirstSet()
      if (firstSet) additionalResources.value.collection = transformEntries(firstSet)
    }
    const showNextPage = async () => {
      const nextSet = getNextSet()
      if (nextSet) additionalResources.value.collection.push(...transformEntries(nextSet))
      await nextTick()
    }
    const doDateSort = () => {
      buffer.value = sortByDateDesc(buffer.value)
      showFirstPage()
    }
    const doRelevancySort = () => {
      doSearch()
    }
    const { sorting, doNonRelevancySort } = useSorting({
      initialSorting: {
        selected: 'Date',
        options: ['Date', 'Relevancy'],
      },
      customSortFunctions: { doDateSort, doRelevancySort },
    })

    const getFilterString = () => {
      const parcedFacets = getORFacetGroupString(queryFacets.value)
      return [
        ...parcedFacets,
        'contentType:resource',
        'NOT resourceGroup:"Press Releases"',
        'NOT resourceGroup:"Events"',
        'NOT resourceGroup:"Ready-to-ship Inventory"',
        'NOT resourceGroup:"FAQ"',
      ].join(' AND ')
    }
    const loadProducts = (hitsPerPage, page) => {
      return fetchResources({
        query: queryString.value,
        filters: getFilterString(),
        hitsPerPage,
        page,
      })
    }
    const doSearch = async () => {
      isLoaderVisible.value = true
      const { hits } = await loadAllEntries(loadProducts)
      resultPanes.value.forEach((el) => {
        el.collection = []
      })
      initPagination(hits)
      showFirstPage()

      const sortedFeatured = arraySort(
        hits.filter((el) => el.isFeatured),
        withSortByFeaturedAndDate(),
      )
      featuredResources.value.collection = transformEntries(sortedFeatured)

      doNonRelevancySort()
      isLoaderVisible.value = false
    }
    const debounceSearch = debounce(function () {
      doSearch()
    }, 500)
    const onFacetUpdate = () => {
      queryFacets.value = getQueryFacetsFromSelection(facets.value)
    }
    const resetQuery = () => {
      queryString.value = ''
      clearFacets(facets.value)
      onFacetUpdate()
    }
    const onHintResetClicked = (e) => {
      if (e.target.id === 'reset-search') resetQuery()
    }
    const onChipDelete = (item) => {
      toggleItemRefinement(item)
      onFacetUpdate()
    }

    setPageMeta(props.cPage?.fields?.pageMeta)

    return {
      noResultsHintVisible,
      isInitialized,
      isLoaderVisible,
      facets,
      noResultsHint,
      queryString,
      queryFacets,
      resultPanes,
      additionalResources,
      featuredResources,
      sorting,
      doNonRelevancySort,
      withSortByFeaturedAndDate,
      transformEntries,
      doSearch,
      showFirstPage,
      fetchResources,
      getFacetsFromUrl,
      groupFacets,
      showNextPage,
      searchWithinFacetValues,
      toggleItemExclusion,
      toggleItemRefinement,
      getQueryFacetsFromSelection,
      getTitleForValue,
      debounceSearch,
      resetQuery,
      onHintResetClicked,
      onChipDelete,
      onFacetUpdate,
      loginGatedResourceData,
      isLoginGatedResourceModalVisible,
      onLoginGatedResourceRequested,
      openLoginGatedResource,
      facetMap,
    }
  },
  async created() {
    if (this.getFacetsFromUrl().length && this.facetMap['types.lvl0'])
      this.facetMap['types.lvl0'].isOpen = false

    const { facets } = await this.fetchResources({
      facets: ['*'],
      filters:
        'NOT resourceGroup:"Press Releases" AND NOT resourceGroup:"Events" AND NOT resourceGroup:"Ready-to-ship Inventory" AND NOT resourceGroup:"FAQ"',
      hitsPerPage: 1,
    })
    this.facets = this.groupFacets(facets, this.getFacetsFromUrl())
    this.onFacetUpdate()
    await this.doSearch()
    this.$watch('queryString', this.debounceSearch)
    this.$watch('queryFacets', this.doSearch)
    this.isInitialized = true
  },
}
</script>
